export interface Ticket {
  _id: string
  ticket_number: string
  account: CustomerProfile | string | null
  assigned_to: AdminUser | null
  created_by: AdminUser | null
  is_internal: boolean
  task_type: string | null
  department: string | null
  subject: string
  status: 'open' | 'in_progress' | 'in_development' | 'waiting_on_customer' | 'follow_up' | 'resolved' | 'closed'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  tags: string[]
  customer_unread: boolean
  admin_unread: boolean
  last_customer_reply: string | null
  last_admin_reply: string | null
  first_response_at: string | null
  resolved_at: string | null
  closed_at: string | null
  email_thread_id: string | null
  source: 'web' | 'email' | 'widget'
  follow_up_date: string | null
  follow_up_note: string
  kanban_order: number
  last_internal_note: string | null
  last_internal_note_at: string | null
  created_at: string
  updated_at: string
}

export interface TicketMessage {
  _id: string
  ticket: string
  sender_type: 'customer' | 'admin' | 'system'
  sender_account: CustomerProfile | null
  sender_admin: AdminUser | null
  message: string
  is_internal_note: boolean
  attachments: Attachment[]
  email_message_id: string | null
  read_by_customer: boolean
  read_by_admin: boolean
  read_by_admins: string[]
  created_at: string
}

export interface Attachment {
  originalname: string
  mimetype: string
  size: number
  bucket: string
  key: string
  location: string
}

export interface TicketTag {
  _id: string
  name: string
  color: string
  description: string
  is_active: boolean
}

export interface AdminUser {
  _id: string
  username: string
  email: string
  departments?: string[]
}

// Department values must match the strings stored on AdminUser.departments so
// the assignee dropdowns can filter staff by department. (Historic ticket rows
// used 'support'/'billing'; those were migrated to customer_service/accounting.)
export const DEPARTMENTS = [
  { value: 'customer_service', label: 'שירות לקוחות' },
  { value: 'development', label: 'תמיכה טכנית' },
  { value: 'accounting', label: 'חשבונות' },
  { value: 'sales', label: 'מכירות' },
]

export interface CustomerProfile {
  _id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  company_name?: string
  support_token?: string
  monthly_payment_amount?: number
  trial_period_end?: string
  created_at?: string
  last_active?: string
  campaigns?: any[]
  bots?: any[]
  facebookUsers?: any[]
  recentActivity?: any[]
  ticketHistory?: Ticket[]
  stats?: {
    totalCampaigns: number
    totalBots: number
    totalProfiles: number
    totalTickets: number
  }
}

export interface TicketStats {
  totalOpen: number
  totalUnassigned: number
  totalUrgent: number
  staleTickets: number
  myTickets: number
  todayCreated: number
  todayResolved: number
  statusBreakdown: Record<string, number>
  avgResponseMinutes: number | null
  followUpsDue: number
}

export interface TicketsQueryResponse {
  tickets: Ticket[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface TicketDetailResponse {
  ticket: Ticket
  messages: TicketMessage[]
  customerProfile: CustomerProfile
}

export type TicketStatus = 'open' | 'in_progress' | 'in_development' | 'waiting_on_customer' | 'follow_up' | 'resolved' | 'closed' | 'all' | 'active'
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent'

export const STATUS_LABELS: Record<string, string> = {
  open: 'פתוח',
  in_progress: 'בטיפול',
  in_development: 'בטיפול פיתוח',
  waiting_on_customer: 'ממתין ללקוח',
  follow_up: 'מעקב',
  resolved: 'נפתר',
  closed: 'סגור',
  all: 'הכל',
  active: 'פעיל'
}

export const PRIORITY_LABELS: Record<string, string> = {
  low: 'נמוך',
  medium: 'בינוני',
  high: 'גבוה',
  urgent: 'דחוף'
}

export const STATUS_COLORS: Record<string, string> = {
  open: 'warning',
  in_progress: 'primary',
  in_development: 'info',
  waiting_on_customer: 'info',
  follow_up: 'warning',
  resolved: 'success',
  closed: 'secondary'
}

export const PRIORITY_COLORS: Record<string, string> = {
  low: 'secondary',
  medium: 'info',
  high: 'warning',
  urgent: 'danger'
}

export const TASK_TYPE_LABELS: Record<string, string> = {
  general: 'כללי',
  call_client: 'התקשר ללקוח',
  check_profiles: 'בדוק פרופילים',
  fix_campaigns: 'תקן קמפיינים',
  onboarding: 'הטמעה',
  billing: 'חשבונות',
  follow_up: 'מעקב',
}

export const TASK_TYPE_ICONS: Record<string, string> = {
  general: 'bi-pencil',
  call_client: 'bi-telephone',
  check_profiles: 'bi-person-check',
  fix_campaigns: 'bi-gear',
  onboarding: 'bi-rocket-takeoff',
  billing: 'bi-credit-card',
  follow_up: 'bi-arrow-repeat',
}

export interface TaskStats {
  myOpenTasks: number
  allOpenTasks: number
  overdueFollowUps: number
  soonFollowUps: number
  upcomingFollowUps: number
  urgency: 'none' | 'pulse' | 'aggressive' | 'overdue'
  needsFlash: boolean
}
